// Вкладка «Интеграции» в настройках: подключение сетей и сервисов — карточки «Сеть и реклама» и «Сервисы».
// Ключ вставляется один раз, платформа сразу проверяет его настоящим запросом и хранит зашифрованным;
// обратно показываются только последние символы.
import { Button, Card, Chip, toast, usd, when } from '@autoaff/ui';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Cloud, Copy, Globe, Megaphone, Mic, Network, RefreshCw, Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import {
  type BuyoIntegration,
  type GeminiIntegration,
  type Integrations,
  type MetaAccountView,
  type MetaIntegration,
  type TelegramIntegration,
  useIntegrations,
} from '../business';
import { backupFailed, fileSize, useSystem } from './BackupCard';

/** Ответ api с одной интеграцией — вклеить в общий список, не дожидаясь перезапроса. */
function merge(qc: QueryClient, part: Partial<Integrations>) {
  qc.setQueryData<Integrations>(['integrations'], (old) => (old ? { ...old, ...part } : old));
}

const PB_RESULT: Record<string, string> = {
  applied: 'статус обновлён',
  nochange: 'без изменений',
  unknown: 'лид не найден',
  received: 'обрабатывается',
  selftest: 'проверка',
  bad_secret: 'неверный секрет',
  blocked: 'заблокирован',
  bad_request: 'без номера лида',
};

/** Адрес постбека: вставить в кабинет buyo — статусы приходят сразу, а не раз в 10 минут. */
function PostbackBox({ url, last }: { url: string; last: { at: string; result: string } | null }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast('Адрес постбека скопирован');
    } catch {
      toast('Не удалось скопировать — выделите адрес и скопируйте вручную');
    }
  };
  return (
    <div className="postback-box">
      <label className="label" htmlFor="buyo-postback">
        Постбек для кабинета buyo
      </label>
      <div className="pb-row">
        <input
          id="buyo-postback"
          className="input mono"
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button size="sm" icon={<Copy />} onClick={copy}>
          Скопировать
        </Button>
      </div>
      <p>
        Вставьте этот адрес в кабинете buyo — статусы лидов будут приходить сразу, а не раз в 10 минут.
        {last
          ? ` Последний постбек: ${when(last.at)}, ${PB_RESULT[last.result] ?? last.result}.`
          : ' Постбеков ещё не было.'}
      </p>
    </div>
  );
}

function BuyoRow({ b }: { b: BuyoIntegration | undefined }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState<'save' | 'sync' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const r = await api<{ buyo: BuyoIntegration }>('/api/integrations/buyo', { apiKey: key });
      merge(qc, r);
      setKey('');
      setOpen(false);
      toast('buyo подключён — каталог и лиды подтянутся в течение пары минут');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const syncNow = async () => {
    setBusy('sync');
    try {
      await api('/api/integrations/buyo/sync', {});
      toast('Обновление запущено');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const err = b?.sync?.lastError ?? b?.catalog?.lastError ?? null;
  const details: string[] = [];
  if (b?.connected) {
    if (b.hint) details.push(`ключ ${b.hint}`);
    if (b.catalog?.offers) details.push(`офферов в каталоге: ${b.catalog.offers}`);
    details.push(`лидов: ${b.leads}`);
    details.push(b.sync?.lastAt ? `статусы обновлены ${when(b.sync.lastAt)}` : 'первая синхронизация идёт');
  }

  return (
    <div className="integ-row" id="buyo">
      <span className="integ-ico">
        <Network />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <b>buyo network</b>
          {b?.connected ? (
            <Chip tone="success" dot>
              Подключено
            </Chip>
          ) : (
            <Chip>Не подключено</Chip>
          )}
        </div>
        <p>
          {b?.connected
            ? details.join(' · ')
            : 'Каталог офферов, потоки, лиды и статусы. Ключ API есть в кабинете buyo (или в Panda-Manda).'}
        </p>
        {err && <p style={{ color: 'var(--warning)' }}>Последняя попытка не удалась: {err}</p>}
        {b?.sync?.stats?.note && <p>{b.sync.stats.note}</p>}
        {b?.connected && b.postback?.url && <PostbackBox url={b.postback.url} last={b.postback.last} />}

        {open ? (
          <form
            className="token-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (key.trim()) void save();
            }}
          >
            <label className="label" htmlFor="buyo-key">
              Ключ API buyo
            </label>
            <input
              id="buyo-key"
              className="input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'buyo-key-error' : undefined}
            />
            {error && (
              <p id="buyo-key-error" className="notice error" style={{ margin: 0 }}>
                {error}
              </p>
            )}
            <div className="row">
              <Button type="submit" variant="primary" disabled={!key.trim() || busy !== null}>
                {busy === 'save' ? 'Проверяю ключ…' : 'Проверить и сохранить'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setOpen(false);
                  setKey('');
                  setError(null);
                }}
              >
                Отмена
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex gap-2 flex-wrap" style={{ marginTop: 10 }}>
            <Button size="sm" variant={b?.connected ? 'default' : 'primary'} onClick={() => setOpen(true)}>
              {b?.connected ? 'Заменить ключ' : 'Подключить'}
            </Button>
            {b?.connected && (
              <Button
                size="sm"
                variant="ghost"
                icon={<RefreshCw />}
                onClick={syncNow}
                disabled={busy !== null}
              >
                Обновить сейчас
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const TIER: Record<string, string> = {
  development_access: 'базовый доступ к API',
  standard_access: 'стандартный доступ к API',
};

function AccountToggle({ a, onChange }: { a: MetaAccountView; onChange: (enabled: boolean) => void }) {
  const id = `acc-${a.id}`;
  return (
    <li className="acc-row">
      <input id={id} type="checkbox" checked={a.enabled} onChange={(e) => onChange(e.target.checked)} />
      <label htmlFor={id}>
        <b>{a.name}</b>
        <span>
          {[a.businessName, a.currency, a.timezoneName].filter(Boolean).join(' · ')}
          {a.enabled && a.insightsSyncedAt ? ` · статистика ${when(a.insightsSyncedAt)}` : ''}
        </span>
        {a.lastError && a.enabled && <span className="warn">{a.lastError}</span>}
      </label>
      {a.status !== 1 && <Chip tone="danger">{a.statusText}</Chip>}
    </li>
  );
}

const AD_ID_TAG = 'utm_content={{ad.id}}';

function MetaRow({ m }: { m: MetaIntegration | undefined }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState<'save' | 'sync' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const r = await api<{ meta: MetaIntegration }>('/api/integrations/meta', { token });
      merge(qc, r);
      setToken('');
      setOpen(false);
      const n = r.meta.accounts.filter((a) => a.enabled).length;
      toast(`Meta подключена: кабинетов ${n}. Статистика за 90 дней подтянется за час-полтора`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const toggle = async (a: MetaAccountView, enabled: boolean) => {
    try {
      const r = await api<{ meta: MetaIntegration }>(
        `/api/integrations/meta/accounts/${a.id}`,
        { enabled },
        'PATCH',
      );
      merge(qc, r);
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const syncNow = async () => {
    setBusy('sync');
    try {
      await api('/api/integrations/meta/sync', {});
      toast('Обновление запущено');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const enabled = m?.accounts.filter((a) => a.enabled) ?? [];
  const details: string[] = [];
  if (m?.connected) {
    if (m.hint) details.push(`токен ${m.hint}`);
    details.push(`кабинетов в работе: ${enabled.length} из ${m.accounts.length}`);
    if (m.accessTier) details.push(TIER[m.accessTier] ?? m.accessTier);
    details.push(
      m.sync.today?.lastAt ? `расход обновлён ${when(m.sync.today.lastAt)}` : 'первая загрузка идёт',
    );
  }
  const err = m?.sync.today?.lastError ?? m?.sync.days?.lastError ?? m?.sync.structure?.lastError ?? null;
  const tagged = m && m.ads.active > 0 ? m.ads.tagged / m.ads.active : null;

  return (
    <div className="integ-row" id="meta">
      <span className="integ-ico">
        <Megaphone />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <b>Реклама Meta</b>
          {m?.connected ? (
            <Chip tone="success" dot>
              Подключено
            </Chip>
          ) : (
            <Chip>Не подключено</Chip>
          )}
        </div>
        <p>
          {m?.connected
            ? details.join(' · ')
            : 'Кабинеты, кампании, объявления, расход и досмотры. Менять рекламу платформа начнёт, только когда вы включите запись в Meta в «Автономии и деньгах».'}
        </p>
        {err && <p style={{ color: 'var(--warning)' }}>Последняя попытка не удалась: {err}</p>}
        {m?.connected && m.accessTier === 'development_access' && (
          <p>
            У приложения базовый доступ к API — лимиты запросов маленькие. Когда наберётся история вызовов,
            подадим заявку на стандартный доступ.
          </p>
        )}
        {tagged !== null && tagged < 0.9 && (
          <p style={{ color: 'var(--warning)' }}>
            ID объявления в ссылке передают {m?.ads.tagged} из {m?.ads.active} активных объявлений. Лиды
            остальных не свяжутся с объявлением: добавьте в «Параметры URL» <code>{AD_ID_TAG}</code>.
          </p>
        )}
        {m?.connected && m.accounts.length > 0 && (
          <ul className="acc-list" aria-label="Рекламные кабинеты">
            {m.accounts.map((a) => (
              <AccountToggle key={a.id} a={a} onChange={(v) => void toggle(a, v)} />
            ))}
          </ul>
        )}
        {open ? (
          <form
            className="token-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (token.trim()) void save();
            }}
          >
            <ol>
              <li>
                developers.facebook.com → Мои приложения → <b>Создать приложение</b>: сценарий «Создание и
                настройка рекламы с помощью Marketing API», на шаге «Компания» — ваше бизнес-портфолио. Без
                приложения в портфолио Meta не даст сгенерировать маркер.
              </li>
              <li>
                business.facebook.com → Настройки компании → Пользователи → <b>Пользователи системы</b> →
                Добавить, роль «Администратор».
              </li>
              <li>
                «Назначить объекты» → Рекламные аккаунты → отметить кабинеты, полный доступ. Там же →
                Приложения → ваше приложение, полный доступ.
              </li>
              <li>
                «Сгенерировать маркер» (это и есть токен) → ваше приложение, срок «Никогда», разрешения{' '}
                <code>ads_read</code>, <code>ads_management</code>, <code>business_management</code>. Meta
                покажет маркер один раз — сразу вставьте его сюда.
              </li>
            </ol>
            <label className="label" htmlFor="meta-token">
              Токен системного пользователя
            </label>
            <input
              id="meta-token"
              className="input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'meta-token-error' : undefined}
            />
            {error && (
              <p id="meta-token-error" className="notice error" style={{ margin: 0 }}>
                {error}
              </p>
            )}
            <div className="row">
              <Button type="submit" variant="primary" disabled={!token.trim() || busy !== null}>
                {busy === 'save' ? 'Проверяю токен…' : 'Проверить и сохранить'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setOpen(false);
                  setToken('');
                  setError(null);
                }}
              >
                Отмена
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex gap-2 flex-wrap" style={{ marginTop: 10 }}>
            <Button size="sm" variant={m?.connected ? 'default' : 'primary'} onClick={() => setOpen(true)}>
              {m?.connected ? 'Заменить токен' : 'Подключить'}
            </Button>
            {m?.connected && (
              <Button
                size="sm"
                variant="ghost"
                icon={<RefreshCw />}
                onClick={syncNow}
                disabled={busy !== null}
              >
                Обновить сейчас
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type ChatRole = 'alerts' | 'leads';

const ROLE_TEXT: Record<ChatRole, { title: string; empty: string }> = {
  alerts: {
    title: 'Алерты и коды входа',
    empty:
      'Создайте группу только из себя, добавьте туда бота администратором и привяжите её. Код входа увидит каждый участник группы.',
  },
  leads: {
    title: 'Лиды и апрувы',
    empty: 'Ваша группа CPA. Пока она не привязана, лиды и апрувы приходят в чат алертов.',
  },
};

function ChatSlot({
  chatRole: role,
  t,
  busy,
  run,
}: {
  chatRole: ChatRole;
  t: TelegramIntegration;
  busy: string | null;
  run: (what: string, fn: () => Promise<void>) => Promise<void>;
}) {
  const qc = useQueryClient();
  const chat = t.chats[role];
  const link = t.links[role];
  const call = (path: string, body: object, method?: string) =>
    run(`${role}:${path}`, async () => {
      merge(qc, await api<{ telegram: TelegramIntegration }>(path, body, method));
    });
  return (
    <li className="tg-chat">
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <b>{ROLE_TEXT[role].title}</b>
          <span>
            {chat
              ? `${chat.type === 'private' ? 'личный чат' : 'группа'} «${chat.title ?? 'без названия'}»`
              : ROLE_TEXT[role].empty}
          </span>
        </div>
        {chat ? (
          <Chip tone="success" dot>
            Привязан
          </Chip>
        ) : (
          <Chip>Не привязан</Chip>
        )}
      </div>
      {/* Что присылать о лидах: пока группа лидов не привязана, это приходит в чат алертов */}
      {role === 'leads' && (chat || t.chats.alerts) && (
        <div className="tg-toggles">
          <label>
            <input
              type="checkbox"
              checked={t.notify.landingLeads}
              onChange={(e) =>
                void call(
                  '/api/integrations/telegram',
                  { notify: { landingLeads: e.target.checked } },
                  'PATCH',
                )
              }
            />
            Лиды с наших лендингов — сразу, с номером
          </label>
          <label>
            <input
              type="checkbox"
              checked={t.notify.approvals}
              onChange={(e) =>
                void call('/api/integrations/telegram', { notify: { approvals: e.target.checked } }, 'PATCH')
              }
            />
            Апрувы — раз в минуту одним сообщением, с выплатой
          </label>
          <label>
            <input
              type="checkbox"
              checked={t.notify.leads}
              onChange={(e) =>
                void call('/api/integrations/telegram', { notify: { leads: e.target.checked } }, 'PATCH')
              }
            />
            Новые лиды из кабинета buyo — списком раз в минуту
          </label>
          {t.notify.leads && (
            <p className="warn">Если Panda-Manda тоже пишет лиды в эту группу, они будут приходить дважды.</p>
          )}
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {chat ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy !== null}
              onClick={() =>
                void run(`${role}:test`, async () => {
                  await api('/api/integrations/telegram/test', { role });
                  toast('Проверочное сообщение отправлено');
                })
              }
            >
              Отправить проверку
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy !== null}
              onClick={() => void call(`/api/integrations/telegram/chat/${role}`, {}, 'DELETE')}
            >
              Отвязать
            </Button>
          </>
        ) : link ? (
          <>
            <a className="btn btn-primary btn-sm" href={link.group} target="_blank" rel="noreferrer">
              Привязать группу
            </a>
            {role === 'alerts' && (
              <a className="btn btn-sm" href={link.private} target="_blank" rel="noreferrer">
                или личный чат
              </a>
            )}
            <p className="tg-manual">
              Бот не ответил «Готово» в группе? Отправьте в группу эту команду: <code>{link.command}</code>{' '}
              <button
                type="button"
                className="auth-link"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(link.command)
                    .then(() => toast('Команда скопирована'))
                    .catch(() => toast('Не получилось скопировать — выделите команду вручную'))
                }
              >
                Скопировать
              </button>
            </p>
          </>
        ) : (
          <Button
            size="sm"
            disabled={busy !== null}
            onClick={() => void call('/api/integrations/telegram/link', { role })}
          >
            Получить ссылку
          </Button>
        )}
      </div>
    </li>
  );
}

function TelegramRow({ t }: { t: TelegramIntegration | undefined }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    try {
      await fn();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      merge(qc, await api<{ telegram: TelegramIntegration }>('/api/integrations/telegram', { token }));
      setToken('');
      setOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="integ-row" id="telegram">
      <span className="integ-ico">
        <Send />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <b>Telegram</b>
          {t?.bound ? (
            <Chip tone="success" dot>
              Подключено
            </Chip>
          ) : t?.connected ? (
            <Chip tone="warning">Осталось привязать чат</Chip>
          ) : (
            <Chip>Не подключено</Chip>
          )}
        </div>
        <p>
          {t?.connected
            ? `Бот @${t.botUsername}. В группах он молчит и только присылает сообщения.`
            : 'Алерты и коды входа, лиды с наших лендингов с номером, апрувы — сразу в телефон. Там же — разговор с агентом и одобрение заявок.'}
        </p>
        {t?.lastError && <p style={{ color: 'var(--warning)' }}>{t.lastError}</p>}
        {t?.connected && (
          <ul className="tg-chats" aria-label="Чаты бота">
            <ChatSlot chatRole="alerts" t={t} busy={busy} run={run} />
            <ChatSlot chatRole="leads" t={t} busy={busy} run={run} />
          </ul>
        )}

        {open ? (
          <form
            className="token-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (token.trim()) void save();
            }}
          >
            <ol>
              <li>
                В Telegram откройте <b>@BotFather</b> → команда <code>/newbot</code> → имя и адрес бота (адрес
                заканчивается на «bot»).
              </li>
              <li>BotFather пришлёт токен вида 123456789:AA… — скопируйте его сюда.</li>
            </ol>
            <label className="label" htmlFor="tg-token">
              Токен бота
            </label>
            <input
              id="tg-token"
              className="input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'tg-token-error' : undefined}
            />
            {error && (
              <p id="tg-token-error" className="notice error" style={{ margin: 0 }}>
                {error}
              </p>
            )}
            <div className="row">
              <Button type="submit" variant="primary" disabled={!token.trim() || busy !== null}>
                {busy === 'save' ? 'Проверяю токен…' : 'Проверить и сохранить'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setOpen(false);
                  setToken('');
                  setError(null);
                }}
              >
                Отмена
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex gap-2 flex-wrap" style={{ marginTop: 10 }}>
            <Button size="sm" variant={t?.connected ? 'default' : 'primary'} onClick={() => setOpen(true)}>
              {t?.connected ? 'Заменить бота' : 'Подключить'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Интеграция по одному ключу API: Gemini (голос, озвучка, оценка роликов), fal.ai (картинки и видео для креативов). */
const KEY_ROWS = {
  gemini: {
    name: 'Gemini',
    icon: Mic,
    label: 'Ключ API Gemini',
    on: 'голосовые агенту в Telegram расшифровываются; озвучка, оценка роликов и описание футажа для креативов',
    off: 'Расшифровка голосовых агенту в Telegram, озвучка роликов, оценка роликов и описание футажа. Ключ — в Google AI Studio.',
    saved: 'Gemini подключён',
  },
  fal: {
    name: 'fal.ai',
    icon: Sparkles,
    label: 'Ключ API fal.ai',
    on: 'картинки и видео для креативов — через платформу, в пределах бюджета батча',
    off: 'Генерации для креативов: картинки и видео из картинки. Ключ — в кабинете fal.ai → API Keys.',
    saved: 'fal.ai подключён — агент может генерировать кадры',
  },
} as const;

function KeyRow({ kind, g }: { kind: keyof typeof KEY_ROWS; g: GeminiIntegration | undefined }) {
  const meta = KEY_ROWS[kind];
  const Icon = meta.icon;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Ключ принят, но сервис предупредил (на fal нет денег) — видно под строкой, пока страница открыта
  const [warning, setWarning] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    setWarning(null);
    try {
      const { warning: w, ...part } = await api<Partial<Integrations> & { warning?: string }>(
        `/api/integrations/${kind}`,
        { apiKey: key },
      );
      merge(qc, part);
      setKey('');
      setOpen(false);
      if (w) setWarning(w);
      else toast(meta.saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="integ-row" id={kind}>
      <span className="integ-ico">
        <Icon />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <b>{meta.name}</b>
          {g?.connected ? (
            <Chip tone="success" dot>
              Подключено
            </Chip>
          ) : (
            <Chip>Не подключено</Chip>
          )}
        </div>
        <p>{g?.connected ? `ключ ${g.hint ?? ''} · ${meta.on}` : meta.off}</p>
        {warning && !open && <p style={{ color: 'var(--warning)' }}>{warning}</p>}
        {open ? (
          <form
            className="token-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (key.trim()) void save();
            }}
          >
            <label className="label" htmlFor={`${kind}-key`}>
              {meta.label}
            </label>
            <input
              id={`${kind}-key`}
              className="input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              aria-invalid={error ? true : undefined}
            />
            {error && (
              <p className="notice error" style={{ margin: 0 }}>
                {error}
              </p>
            )}
            <div className="row">
              <Button type="submit" variant="primary" disabled={!key.trim() || busy}>
                {busy ? 'Проверяю ключ…' : 'Проверить и сохранить'}
              </Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Отмена
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex gap-2 flex-wrap" style={{ marginTop: 10 }}>
            <Button size="sm" variant={g?.connected ? 'default' : 'primary'} onClick={() => setOpen(true)}>
              {g?.connected ? 'Заменить ключ' : 'Подключить'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Прокси для скачивания футажа с YouTube, Instagram и TikTok: с сервера напрямую они отвечают плохо. */
function ProxyRow({ p }: { p: Integrations['proxy'] }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = () => {
    setPrice(p?.pricePerGb ? String(p.pricePerGb) : '');
    setOpen(true);
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      merge(qc, await api<Partial<Integrations>>('/api/integrations/proxy', { url, pricePerGb: price || 0 }));
      setUrl('');
      setOpen(false);
      toast('Прокси сохранён — YouTube, Instagram и TikTok пойдут через него');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const off = async () => {
    setBusy(true);
    try {
      merge(qc, await api<Partial<Integrations>>('/api/integrations/proxy', undefined, 'DELETE'));
      toast('Прокси отключён — всё скачивается напрямую');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="integ-row" id="proxy">
      <span className="integ-ico">
        <Globe />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <b>Прокси для футажа</b>
          {p?.connected ? (
            <Chip tone="success" dot>
              Подключено
            </Chip>
          ) : (
            <Chip>Не подключено</Chip>
          )}
        </div>
        <p>
          {p?.connected
            ? `${p.hint ?? ''} · YouTube, Instagram и TikTok скачиваются через него${p.pricePerGb ? `, ${usd(p.pricePerGb)} за ГБ идёт в расходы` : ''}`
            : 'YouTube, Instagram и TikTok с сервера отвечают плохо — агент качает футаж через резидентный прокси. Адрес — в кабинете провайдера прокси.'}
        </p>
        {open ? (
          <form
            className="token-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (url.trim()) void save();
            }}
          >
            <label className="label" htmlFor="proxy-url">
              Адрес прокси
            </label>
            <input
              id="proxy-url"
              className="input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="http://логин:пароль@хост:порт"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-invalid={error ? true : undefined}
            />
            <label className="label" htmlFor="proxy-price">
              Цена за ГБ, $
            </label>
            <input
              id="proxy-price"
              className="input tnum"
              inputMode="decimal"
              placeholder="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              style={{ maxWidth: 140 }}
            />
            {error && (
              <p className="notice error" style={{ margin: 0 }}>
                {error}
              </p>
            )}
            <div className="row">
              <Button type="submit" variant="primary" disabled={!url.trim() || busy}>
                {busy ? 'Сохраняю…' : 'Сохранить прокси'}
              </Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Отмена
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex gap-2 flex-wrap" style={{ marginTop: 10 }}>
            <Button size="sm" variant={p?.connected ? 'default' : 'primary'} onClick={start}>
              {p?.connected ? 'Заменить прокси' : 'Подключить'}
            </Button>
            {p?.connected && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void off()}>
                Отключить прокси
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Cloudflare R2: ключи R2 есть только у контейнера бэкапа на сервере, поэтому здесь не форма, а правда
 * из итога последнего бэкапа — куда ушла копия базы. Следом каждую ночь туда же уходят новые медиа, репозиторий
 * релизов и зашифрованные настройки сервера (подробно — в «Бэкапах и хранилище»).
 */
function R2Row() {
  const q = useSystem();
  const o = q.data?.storage?.offsite ?? null;
  const media = q.data?.storage?.media ?? null;
  const failed = backupFailed(q.data?.backup);
  const mediaOff = Boolean(q.data?.backup?.media?.at);
  const onDisk = media
    ? ` Медиа (ролики, футаж, картинки) — ${fileSize(media.bytes)} на диске сервера${mediaOff ? '; новые файлы каждую ночь тоже уходят в R2' : ''}.`
    : '';
  return (
    <div className="integ-row" id="r2">
      <span className="integ-ico">
        <Cloud />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <b>Cloudflare R2</b>
          {q.isPending ? null : o ? (
            <Chip tone="success" dot>
              Подключено
            </Chip>
          ) : (
            <Chip>Не подключено</Chip>
          )}
        </div>
        {q.isPending ? (
          <div className="skeleton" style={{ height: 18, marginTop: 6, maxWidth: 420 }} />
        ) : q.isError ? (
          <p>Не удалось узнать, куда уходят копии базы. {(q.error as Error).message}</p>
        ) : o ? (
          <p>
            Каждую ночь копия базы, зашифрованная вашим ключом, уходит
            {o.bucket ? ` в бакет ${o.bucket}` : ' в R2'}. Последняя —{' '}
            {o.at ? when(o.at) : 'после запуска на сервере'}
            {o.size ? `, ${fileSize(o.size)}` : ''}.{onDisk}
          </p>
        ) : (
          <p>
            Копии базы лежат только на диске сервера. R2 подключается на сервере: ключ доступа к бакету и ваш
            открытый ключ шифрования задаются там, а не здесь.{onDisk}
          </p>
        )}
        {failed && (
          <p style={{ color: 'var(--warning)' }}>
            Последняя попытка не удалась: {q.data?.backup?.lastError ?? 'ошибка'}
          </p>
        )}
        <div className="flex gap-2 flex-wrap" style={{ marginTop: 10 }}>
          <Link to="/settings" search={{ tab: 'system' }} hash="backup" className="btn btn-sm">
            Открыть бэкапы и хранилище
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Сеть и реклама: откуда офферы и лиды, куда тратятся деньги. */
export function NetworkCard() {
  const q = useIntegrations();
  return (
    <Card title="Сеть и реклама">
      <div className="flex flex-col">
        <BuyoRow b={q.data?.buyo} />
        <MetaRow m={q.data?.meta} />
      </div>
    </Card>
  );
}

/** Сервисы: связь с владельцем, ключи для креативов, прокси для футажа и хранилище копий базы. */
export function ServicesCard() {
  const q = useIntegrations();
  return (
    <Card title="Сервисы">
      <div className="flex flex-col">
        <TelegramRow t={q.data?.telegram} />
        <KeyRow kind="gemini" g={q.data?.gemini} />
        <KeyRow kind="fal" g={q.data?.fal} />
        <ProxyRow p={q.data?.proxy} />
        <R2Row />
      </div>
    </Card>
  );
}
